import React, { useEffect, useState } from "react";
import {
  Sparkles, Loader2, CreditCard, RefreshCcw, CircleDollarSign, XCircle, CalendarClock,
  CheckCircle2, IndianRupee, ExternalLink, AlertTriangle,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/api/client";
import { Link } from "react-router-dom";

const STATUS_STYLES = {
  active:    "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  cancelled: "border-slate-700 text-slate-500",
  halted:    "border-red-500/40 text-red-300 bg-red-500/5",
  pending:   "border-amber-500/40 text-amber-300 bg-amber-500/5",
  completed: "border-slate-700 text-slate-500",
  failed:    "border-red-500/40 text-red-300 bg-red-500/5",
  created:   "border-slate-700 text-slate-400",
  captured:  "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
};

const rupees = (paise) => (Number(paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const dateFmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function SubscriptionManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [banner, setBanner] = useState({ msg: "", tone: "ok" }); // ok | err
  const flash = (msg, tone = "ok") => {
    setBanner({ msg, tone });
    setTimeout(() => setBanner({ msg: "", tone }), 4000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/razorpay/subscription/manage");
      setData(resp);
    } catch (e) {
      flash(formatApiErrorDetail(e.response?.data?.detail) || "Could not load subscription", "err");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const cancelRecurring = async (subId) => {
    if (!window.confirm(
      "Cancel this subscription?\n\nYou'll keep access until the end of the current billing cycle. No further charges will be made."
    )) return;
    setCancelling(true);
    try {
      await api.post(`/razorpay/subscription/${subId}/cancel`, { cancel_at_cycle_end: true });
      flash("Subscription cancelled. Access continues until the end of this cycle.");
      await load();
    } catch (e) {
      flash(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed", "err");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="subscription-manager" className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
          <p className="text-sm text-slate-500">Loading your subscription…</p>
        </div>
      </div>
    );
  }

  const active = data?.active;
  const isRecurring = active?.type === "recurring";
  const isOneTime = active?.type === "one_time";
  const isCancelled = active?.status === "cancelled" || active?.cancel_at_cycle_end;

  return (
    <div data-testid="subscription-manager" className="border border-slate-800 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <Sparkles className="w-5 h-5 text-sky-400" />
        <div>
          <h3 className="font-serif text-xl text-sky-100">Subscription</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Manage your active plan, see renewal date, and view past charges.
          </p>
        </div>
      </div>

      {banner.msg && (
        <div
          data-testid="subscription-banner"
          className={`mb-4 px-3 py-2 rounded text-xs font-mono border ${
            banner.tone === "err"
              ? "border-red-500/40 bg-red-500/5 text-red-300"
              : "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
          }`}
        >
          {banner.msg}
        </div>
      )}

      {/* ---------- Active subscription card ---------- */}
      {!active && (
        <div data-testid="subscription-none" className="border border-slate-800 rounded-lg p-6 bg-gradient-to-b from-slate-900/40 to-transparent text-center">
          <CircleDollarSign className="w-8 h-8 mx-auto text-slate-600 mb-3" />
          <p className="text-sm text-slate-300">No active subscription yet.</p>
          <p className="text-xs text-slate-500 mt-1">Pick a plan to unlock unlimited content generation.</p>
          <Link
            to="/pricing"
            data-testid="btn-view-pricing"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium text-sm"
          >
            View Pricing <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {active && (
        <div data-testid="subscription-active" className="border border-sky-500/30 rounded-lg p-5 bg-gradient-to-br from-sky-500/5 to-transparent">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-serif text-2xl text-sky-100 capitalize">{active.plan_slug}</p>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_STYLES[active.status] || "border-slate-700 text-slate-400"}`}>
                  ● {active.status}
                </span>
                {isRecurring && (
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-sky-500/40 text-sky-300">
                    <RefreshCcw className="inline w-2.5 h-2.5 mr-1" /> auto-renew
                  </span>
                )}
                {isOneTime && (
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-slate-700 text-slate-400">
                    one-time
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Billed <span className="text-sky-300 font-mono">{active.billing_cycle}</span>
                {" · "}
                <IndianRupee className="inline w-3 h-3" />
                {rupees(active.amount)}
              </p>
            </div>
          </div>

          {/* Key dates */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
            <Stat label="Started" value={dateFmt(active.starts_at)} icon={CheckCircle2} />
            <Stat
              label={isRecurring && !isCancelled ? "Next renewal" : "Access until"}
              value={dateFmt(active.ends_at)}
              icon={CalendarClock}
              highlight
            />
            {active.total_count && (
              <Stat label="Commitment" value={`${active.total_count} × ${active.billing_cycle}`} icon={RefreshCcw} />
            )}
          </div>

          {isCancelled && (
            <div className="mt-4 flex items-start gap-2 border border-amber-500/40 bg-amber-500/5 rounded p-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
              <p className="text-xs text-amber-200">
                This subscription is set to cancel at the end of the current cycle
                {active.ends_at ? ` on ${dateFmt(active.ends_at)}` : ""}.
                You'll keep access until then — no further charges will be made.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {isRecurring && !isCancelled && (
              <button
                data-testid="btn-cancel-subscription"
                onClick={() => cancelRecurring(active.razorpay_subscription_id)}
                disabled={cancelling}
                className="px-4 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded text-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                Cancel subscription
              </button>
            )}
            <Link
              to="/pricing"
              data-testid="btn-change-plan"
              className="px-4 py-2 border border-slate-700 text-slate-300 hover:text-sky-300 hover:border-sky-500/40 rounded text-sm inline-flex items-center gap-2"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Change plan
            </Link>
          </div>
        </div>
      )}

      {/* ---------- Payment history ---------- */}
      {(data?.payments?.length > 0) && (
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <CreditCard className="w-3 h-3" /> Recent charges
          </p>
          <div data-testid="payment-history" className="border border-slate-800 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 font-mono text-[9px]">Order ID</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.razorpay_order_id} data-testid={`payment-row-${p.razorpay_order_id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-3 py-2 text-xs text-slate-400 font-mono whitespace-nowrap">{dateFmt(p.created_at)}</td>
                    <td className="px-3 py-2 text-sky-100 capitalize">{p.plan_slug} <span className="text-slate-500 text-[11px]">· {p.billing_cycle}</span></td>
                    <td className="px-3 py-2 text-sky-100 font-mono">
                      <IndianRupee className="inline w-3 h-3" />{rupees(p.amount)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[p.status] || "border-slate-700 text-slate-400"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-slate-600 font-mono">{p.razorpay_order_id?.slice(0, 22)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Past subscriptions (compact) */}
      {(data?.past_subscriptions?.length > 0) && (
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Past subscriptions</p>
          <div className="space-y-1.5">
            {data.past_subscriptions.map((s, i) => (
              <div key={s.razorpay_subscription_id || s.payment_id || i} className="text-xs text-slate-400 flex items-center gap-2 border border-slate-800 rounded px-3 py-2">
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[s.status] || "border-slate-700 text-slate-400"}`}>
                  {s.status}
                </span>
                <span className="capitalize text-sky-100">{s.plan_slug}</span>
                <span className="text-slate-500">· {s.billing_cycle}</span>
                <span className="text-slate-500 ml-auto font-mono">{dateFmt(s.starts_at)} → {dateFmt(s.ends_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`border rounded p-3 ${highlight ? "border-sky-500/40 bg-sky-500/5" : "border-slate-800"}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </p>
      <p className="text-sm text-sky-100 font-medium mt-1">{value}</p>
    </div>
  );
}
